import React, { useEffect } from 'react';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './style.css';
import { useLocation, Link } from 'react-router-dom';
import {
  withAdminMail,
  withAccount,
  withTranslation,
  withFormValidation,
  withLanguage,
  withUserCommunicationTypes,
} from '../../../hoc';
import { FormValidationContext } from '../../../hoc/Preload/FormValidation';
import {
  Form,
  Input,
  Button,
  RichTextEditor,
  Select,
  ButtonLink,
} from '../../../common/FormItems';
import MultiSelect from 'components/common/MultiSelect';
import { setMsgInfo } from '../../../../actions';
import { useDispatch } from 'react-redux';
import { Icon, Popup } from 'semantic-ui-react';
import _ from 'lodash';
import Swal from 'sweetalert2';

const Wrapper = styled.div``;

const Flex = styled.div`
  display: flex;
  flex-direction: ${({ flexDirection }) => flexDirection || 'row'};
  justify-content: ${({ justifyContent }) => justifyContent || ''};
  align-items: ${({ alignItems }) => alignItems || ''};
  flex-wrap: wrap;
`;

const FileAvatar = styled.a`
  margin: 5px 5px;
  font-style: italic;
  font-size: 0.9rem;
  align-items: center;
  display: block;
  &:last-child {
    margin-top: 0;
  }
`;

Flex.displayName = 'Flex';

const FormContainer = styled.div`
  position: relative;
  background: var(--admincat-color-grey-1);
  border-radius: 10px;
  padding: 1.5rem 1.75rem 1rem;
`;

const BackLinkRow = styled.div`
  margin-bottom: 1rem;
`;

const StyledButtonLink = styled(ButtonLink)`
  span {
    text-transform: uppercase;
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 1.5rem;

  .full-width {
    grid-column: 1 / -1;
  }

  @media only screen and (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const ActionsRow = styled(Flex)`
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--admincat-color-grey-2);
  gap: 1rem;
`;

const AttachmentsPanel = styled.div`
  background: var(--admincat-color-grey-1);
  border-radius: 10px;
  padding: 1.5rem 1.75rem 1rem;
`;

const StyledButton = styled(Button)`
  && {
    // color: var(--admincat-dark-color) !important;
    text-transform: capitalize;
    width: 8.0625rem;
    height: 2.5rem;
    font-weight: 500;
    border-radius: 7px;
    transition: all ease-in-out 300ms;
    background: #4c81be;
    border: 1px solid #1d579b;
    box-sizing: border-box;
    font-style: normal;
    font-size: 1rem;
    line-height: 1rem;
    color: #ffffff;
    cursor: pointer;
    margin-right: 1rem;
  }
`;

const mailFormSchema = {
  from_mail: {
    validation: 'required|email',
    message: {
      email: "Sender's email is invalid",
      required: "Sender's email is required",
    },
  },
  language: {
    validation: 'required',
    message: {
      required: 'language is required',
    },
  },
  subject: {
    validation: 'required|min:3',
  },
  message: {
    validation: 'required|min:3',
    Link,
  },
  user_types: {
    validation: 'required|min:3',
  },
};

const MAX_SIZE_OF_ATTACHMENTS = 8485760; // this is the file size for attachments the django mail client can handle without crashing

/**
 * User Classification IDs
 * These values are determined by the backend and should NOT be modified.
 * Any changes here must be coordinated with the backend to ensure consistency.
 */
const getFields = (formData) => {
  return [
    {
      type: 'email',
      name: 'from_mail',
      label: 'From',
      placeholder: 'Your email',
      disabled: true,
      required: true,
    },
    {
      type: 'text',
      name: 'subject',
      label: 'Subject',
      placeholder: 'Subject',
      required: true,
    },
    {
      type: 'select',
      name: 'language',
      label: 'Language',
      required: true,
      options: formData?.activeLanguageOptions,
    },
    {
      type: 'multi_select',
      name: 'user_types',
      label: 'Users',
      placeholder: '',
      required: true,
      sort: false,
      options: [
        { id: formData.userTypes.UNVERIFIED_USERS, name: 'Unverified Users' },
        { id: formData.userTypes.TRIAL_USERS, name: 'Trial Users' },
        {
          id: formData.userTypes.NON_RENEWED_TRIAL_USERS,
          name: 'Non-Renewed Trial Users',
        },
        { id: formData.userTypes.ACTIVE_SUBSCRIBERS, name: 'Active Subscribers' },
        {
          id: formData.userTypes.AWAITING_RENEWAL,
          name: 'Members Awaiting Renewal',
        },
      ],
    },
    {
      type: 'textarea',
      name: 'message',
      label: 'Message',
      required: true,
      placeholder: 'Your message...',
    },
  ].map((data) => ({
    ...formData,
    ...data,
    value: formData[data.name],
  }));
};

const InputFieldType = {
  email: Input,
  text: Input,
  textarea: RichTextEditor,
  select: Select,
  multi_select: MultiSelect,
};

const FULL_WIDTH_FIELD_TYPES = ['select', 'multi_select', 'textarea'];

const Fields = (props) => {
  const otherFields = getFields({ ...props });

  return (
    <FieldsGrid>
      {otherFields.map(({ type, label, tr, error, name, required, ...rest }) => {
        const InputField = InputFieldType[type];
        return (
          <div
            key={label}
            className={FULL_WIDTH_FIELD_TYPES.includes(type) ? 'full-width' : ''}
          >
            <InputField
              name={name}
              type={type}
              label={label}
              required={required}
              error={error[name] ?? ''}
              {...rest}
            />
          </div>
        );
      })}
    </FieldsGrid>
  );
};

const Compose = (props) => {
  const {
    openComposeForm,
    toggleComposeForm,
    onMailChange: onChange,
    mailFormData,
    sendMail,
    sendMailLoading,
    account: {
      user: { email: personalEmail },
    },
    tr,
    basePath,
    activeLanguageOptions,
    userTypes,
  } = props;
  const location = useLocation();
  const { selectedMail = {}, type } = location.state || {};

  const dispatch = useDispatch();

  // eslint-disable-next-line no-unused-vars
  const { validateForm: _validateForm } = React.useContext(FormValidationContext);

  const from_mail = personalEmail;

  // Create a reference to the hidden file input element
  const hiddenFileInput = React.useRef(null);
  const [uploadedFiles, setUploadedFiles] = React.useState([]);
  const [error, setError] = React.useState({});

  const handleFileUploadClick = (event) => {
    hiddenFileInput.current.click();
  };

  const handleFileChange = (event) => {
    const chosenFiles = Array.prototype.slice.call(event.target.files);
    handleUploadFiles(chosenFiles);
  };

  const handleUploadFiles = (files) => {
    const uploaded = [...uploadedFiles];
    let size = 0;
    files.some((file) => {
      if (uploaded.findIndex((f) => f.name === file.name) === -1) {
        uploaded.push(file);
        return true;
      }
      return false;
    });

    uploaded.map((file) => (size += file.size));
    if (size > MAX_SIZE_OF_ATTACHMENTS) {
      dispatch(
        setMsgInfo({
          success: false,
          msg: [`${tr('Attachments size must be less than 8 MB.')}`],
        }),
      );
      return;
    }

    setUploadedFiles(uploaded);
    onChange({ target: { name: 'attachments', value: files } });
    dispatch(setMsgInfo({ 
      success: true, 
      msg: [`${tr('Files have been appended.')}`],
    }));
  };

  const removeFile = (fileName) => {
    const files = uploadedFiles.filter((file) => file.name !== fileName);
    setUploadedFiles(files);
    onChange({ target: { name: 'attachments', value: files } });
  };

  const handleSubmission = (e) => {
    e.preventDefault();

    // ensure all fields are valid
    const validationError = {};
    for (const key in mailFormSchema) {
      // ensure each field is provided for
      if (!mailFormData[key]) {
        validationError[key] = true;
      }
    }

    if (!_.isEmpty(validationError)) {
      setError(validationError);
      Swal.fire(tr('Warning!'), tr(`Please fill all fields.`), 'warning');
      return;
    }

    sendMail();
  };

  useEffect(() => {
    if (personalEmail) {
      onChange({ target: { name: 'from_mail', value: from_mail } });
    }
    if (type === 'fwd') {
      onChange({
        target: { name: 'subject', value: `Fwd: ${selectedMail?.subject}` },
      });
    }
    if (type === 'reply') {
      onChange({
        target: { name: 'subject', value: `Re: ${selectedMail?.subject}` },
      });
      onChange({ target: { name: 'to_mail', value: selectedMail?.from_mail } });
    }
    return () => null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalEmail, mailFormData.from_mail]);

  return (
    <Wrapper key={basePath} className="form-wrapper">
      <BackLinkRow>
        <StyledButtonLink
          to={{
            pathname: `/dashboard`,
          }}
          className="option-btn"
          icon="angle double left"
          name="Go Back"
        />
      </BackLinkRow>
      <div className="row mt-30">
        <div className="col-7 ml-10">
          <FormContainer>
            <Form
              title="Compose Mail"
              open={openComposeForm}
              toggleForm={() => toggleComposeForm(null)}
              formData={mailFormData}
              validationSchema={mailFormSchema}
              renderActionButtons={() => null}
            >
              <Fields
                {...mailFormData}
                userTypes={userTypes}
                selectedMail={selectedMail}
                tr={tr}
                activeLanguageOptions={activeLanguageOptions}
                onChange={onChange}
                key={openComposeForm}
                error={error}
              />
              <ActionsRow justifyContent="flex-end" alignItems="center">
                <Popup
                  content={tr('Attach documents')}
                  size="mini"
                  trigger={
                    <Icon
                      name="attach"
                      onClick={handleFileUploadClick}
                      size="large"
                      style={{ cursor: 'pointer' }}
                    />
                  }
                />
                <StyledButton
                  onClick={handleSubmission}
                  name="Send"
                  isLoading={sendMailLoading}
                />
                <input
                  type="file"
                  ref={hiddenFileInput}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  multiple
                />
              </ActionsRow>
            </Form>
          </FormContainer>
        </div>
        {uploadedFiles.length > 0 && (
          <div className="col-4 ml-10">
            <AttachmentsPanel>
              <Form
                title={`${tr('Attachments')}(${uploadedFiles.length})`}
                renderActionButtons={null}
              >
                <Flex justifyContent="flex-start" flexDirection="column">
                  {uploadedFiles.map((data, index) => {
                    return (
                      <FileAvatar key={data?.name}>
                        <Icon name="attach" /> {data?.name}{' '}
                        <Popup
                          content={tr('Remove this file')}
                          size="mini"
                          trigger={
                            <Icon
                              name="times"
                              onClick={() => {
                                removeFile(data?.name);
                              }}
                              circular
                              inverted
                              color="red"
                              size="small"
                            />
                          }
                        />
                      </FileAvatar>
                    );
                  })}
                </Flex>
              </Form>
            </AttachmentsPanel>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default compose(
  withAdminMail,
  withAccount,
  withTranslation,
  withFormValidation,
  withLanguage,
  withUserCommunicationTypes,
)(Compose);
